import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Lock,
  Phone,
  X,
  CreditCard,
  Check,
  AlertCircle,
  FileText,
  Calendar,
  Loader2,
} from "lucide-react";
import { ClubMiaAuthService, DniLookupService } from "../../../services";
import {
  DNI,
  CARNET,
  DNI_NOMBRE,
  CARNET_NOMBRE,
  DNI_LONGITUD,
  CARNET_LONGITUD
} from "../../../utils/constants";

const RegistrationForm = ({
  onRegistrationSuccess,
  onNavigateToLogin,
  onGoToHome,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    // password: "", // Commented out - may be needed later
    // confirmPassword: "", // Commented out - may be needed later
    phone: "",
    identification_type: DNI, // Por defecto DNI
    identification_number: "",
    birthDate: "",
  });

  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDniLookupLoading, setIsDniLookupLoading] = useState(false);
  const [dniLookupMessage, setDniLookupMessage] = useState("");

  // Función específica para procesar errores de la API SIAN
  const processApiErrors = (apiResponse) => {
    // Verificar si hay errores en error.data.errors
    const validationErrors = apiResponse?.error?.data?.errors;

    if (!validationErrors || Object.keys(validationErrors).length === 0) {
      const errorMessage = typeof apiResponse?.message === 'string'
        ? apiResponse.message
        : 'Error en el registro';
      return {
        hasValidationErrors: false,
        message: errorMessage
      };
    }

    // Crear mensajes de error amigables
    const errorMessages = [];
    const fieldErrors = {};

    // Mapeo de nombres de campos de la API a nombres amigables
    const fieldNames = {
      'maternal': 'Apellido Materno',
      'firstName': 'Nombre',
      'lastName': 'Apellido Paterno',
      'dni': 'DNI',
      'phone': 'Teléfono',
      'password': 'Contraseña',
      'email': 'Email'
    };

    // Procesar cada campo con errores
    for (const [field, errors] of Object.entries(validationErrors)) {
      if (Array.isArray(errors)) {
        const fieldDisplayName = fieldNames[field] || field;

        for (const error of errors) {
          // Convertir errores a mensajes amigables
          let cleanError = error
            .replace(/A\.\s*Materno/g, 'Apellido Materno')
            .replace(/A\.\s*Paterno/g, 'Apellido Paterno')
            .replace(/no puede ser nulo/g, 'es obligatorio')
            .replace(/tiene caracteres no válidos/g, 'contiene caracteres no permitidos');

          // Combinar nombre del campo con el mensaje de error
          const fullMessage = `${fieldDisplayName}: ${cleanError}`;
          errorMessages.push(`• ${fullMessage}`);

          // Mapear a campos del formulario para mostrar errores específicos
          if (field === 'maternal') {
            fieldErrors['lastName'] = fullMessage;
          } else if (field === 'firstName') {
            fieldErrors['firstName'] = fullMessage;
          } else if (field === 'dni') {
            fieldErrors['dni'] = fullMessage;
          } else if (field === 'phone') {
            fieldErrors['phone'] = fullMessage;
          } else if (field === 'password') {
            fieldErrors['password'] = fullMessage;
          }
        }
      }
    }

    return {
      hasValidationErrors: true,
      message: `Errores específicos:\n${errorMessages.join('\n')}`,
      fieldErrors: fieldErrors
    };
  };

  // DNI lookup function - ONLY for DNI type documents
  const performDniLookup = async (dni) => {
    // Only perform lookup for DNI type documents
    if (formData.identification_type !== DNI || !DniLookupService.isDniReadyForLookup(dni)) {
      return;
    }

    setIsDniLookupLoading(true);
    setDniLookupMessage("");

    try {
      const response = await DniLookupService.searchByDni(dni);

      if (response.success && response.data) {
        const formattedData = DniLookupService.formatPersonDataForForm(response.data);

        // Update form data with the retrieved information
        setFormData(prevData => ({
          ...prevData,
          firstName: formattedData.firstName || prevData.firstName,
          lastName: formattedData.lastName || prevData.lastName,
          // Keep the current identification data
          identification_type: formattedData.identification_type || prevData.identification_type,
          identification_number: formattedData.identification_number || prevData.identification_number,
        }));

        // setDniLookupMessage("✓ Datos encontrados y completados automáticamente");

        // Clear any previous errors for the fields that were populated
        setErrors(prevErrors => ({
          ...prevErrors,
          firstName: "",
          lastName: "",
          identification_number: ""
        }));
      } else {
        setDniLookupMessage(response.message || "No se encontraron datos para este DNI");
      }
    } catch (error) {
      console.error('Error during DNI lookup:', error);
      setDniLookupMessage("Error al buscar datos del DNI");
    } finally {
      setIsDniLookupLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });

    // Clear DNI lookup message when user starts typing or changes document type
    if (name === 'identification_number') {
      setDniLookupMessage("");
    }

    // Clear DNI lookup data when switching from DNI to another document type
    if (name === 'identification_type' && value !== DNI) {
      setDniLookupMessage("");
      setIsDniLookupLoading(false);
    }
  };

  // Effect to trigger DNI lookup when identification number is complete
  useEffect(() => {
    // Only perform lookup for DNI type and when we have exactly 8 digits
    if (formData.identification_type === DNI &&
        DniLookupService.isDniReadyForLookup(formData.identification_number)) {

      // Add a small delay to avoid too many API calls while typing
      const timeoutId = setTimeout(() => {
        performDniLookup(formData.identification_number);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [formData.identification_number, formData.identification_type]);

  const validateForm = () => {
    let newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "El nombre es obligatorio.";
    if (!formData.lastName.trim())
      newErrors.lastName = "El apellido es obligatorio.";

    // Password validation commented out - may be needed later
    // if (!formData.password) {
    //   newErrors.password = "La contraseña es obligatoria.";
    // } else if (formData.password.length < 6) {
    //   newErrors.password = "La contraseña debe tener al menos 6 caracteres.";
    // }
    // if (formData.password !== formData.confirmPassword) {
    //   newErrors.confirmPassword = "Las contraseñas no coinciden.";
    // }

    if (!formData.phone.trim()) newErrors.phone = "El teléfono es obligatorio.";
    if (!formData.identification_number.trim()) {
      newErrors.identification_number = `El ${formData.identification_type === DNI ? DNI_NOMBRE : CARNET_NOMBRE} es obligatorio.`;
    } else {
      const documentLength = formData.identification_type === DNI ? DNI_LONGITUD : CARNET_LONGITUD;
      const documentName = formData.identification_type === DNI ? DNI_NOMBRE : CARNET_NOMBRE;

      if (formData.identification_type === DNI) {
        // Validación para DNI: exactamente 8 dígitos numéricos
        if (formData.identification_number.trim().length !== documentLength || !/^\d+$/.test(formData.identification_number.trim())) {
          newErrors.identification_number = `El ${documentName} debe tener ${documentLength} dígitos numéricos.`;
        }
      } else {
        // Validación para Carnet de Extranjería: hasta 12 caracteres alfanuméricos
        if (formData.identification_number.trim().length > documentLength) {
          newErrors.identification_number = `El ${documentName} no puede tener más de ${documentLength} caracteres.`;
        }
      }
    }

    // Validación de fecha de nacimiento (opcional)
    if (formData.birthDate.trim()) {
      const birthDate = new Date(formData.birthDate);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (birthDate > today) {
        newErrors.birthDate = "La fecha de nacimiento no puede ser futura.";
      } else if (age < 18) {
        newErrors.birthDate = "Debes ser mayor de 18 años para registrarte.";
      } else if (age > 120) {
        newErrors.birthDate = "Por favor, verifica la fecha de nacimiento.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      try {
        console.log('Form data before sending to service:', formData);

        const response = await ClubMiaAuthService.register(formData);

        if (response.success) {
          setMessage("¡Registro exitoso! Bienvenido a Club Mia.");
          setErrors({}); // Limpiar errores
          setTimeout(() => {
            // Pass the complete response data including user and person
            onRegistrationSuccess(response);
          }, 2000);
        } else {
          console.log('🚨 Registration failed, processing errors...');

          // Usar la nueva función para procesar errores
          const errorResult = processApiErrors(response);

          if (errorResult.hasValidationErrors) {
            // Hay errores de validación específicos
            setErrors(errorResult.fieldErrors || {});
            setMessage(typeof errorResult.message === 'string' ? errorResult.message : 'Error en el registro');
          } else {
            // Error general
            setMessage(typeof errorResult.message === 'string' ? errorResult.message : 'Error en el registro');
          }
        }
      } catch (error) {
        console.error("Registration error:", error);
        setMessage("Error de conexión. Inténtalo de nuevo.");
      } finally {
        setIsLoading(false);
      }
    } else {
      setMessage("Por favor, corrige los errores del formulario.");
    }
  };

  return (
    <motion.div
      className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-8 shadow-xl relative"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* Close Button */}
      <motion.button
        onClick={onGoToHome}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <X className="w-6 h-6" />
      </motion.button>

      {/* Header */}
      <div className="text-center mb-8">
        <motion.h2
          className="text-3xl font-bold text-gray-900 mb-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          Únete a Club Mia
        </motion.h2>
        <motion.p
          className="text-gray-600"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Crea tu cuenta y disfruta de beneficios exclusivos
        </motion.p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Tipo de Documento - MOVED TO FIRST */}
        <div>
          <label htmlFor="identification_type" className="sr-only">
            Tipo de Documento
          </label>
          <div className="relative">
            <FileText className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              id="identification_type"
              name="identification_type"
              value={formData.identification_type}
              onChange={handleChange}
              className="mia-input w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl transition-all duration-300 text-gray-900 appearance-none"
            >
              <option value={DNI}>{DNI_NOMBRE}</option>
              <option value={CARNET}>{CARNET_NOMBRE}</option>
            </select>
          </div>
        </div>

        {/* Número de Documento - MOVED TO SECOND */}
        <div>
          <label htmlFor="identification_number" className="sr-only">
            {formData.identification_type === DNI ? DNI_NOMBRE : CARNET_NOMBRE}
          </label>
          <div className="relative">
            <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              id="identification_number"
              name="identification_number"
              value={formData.identification_number}
              onChange={handleChange}
              placeholder={
                formData.identification_type === DNI
                  ? `${DNI_NOMBRE} (${DNI_LONGITUD} dígitos)`
                  : `${CARNET_NOMBRE} (hasta ${CARNET_LONGITUD} caracteres)`
              }
              className={`mia-input w-full pl-12 pr-4 py-3 bg-gray-50 border ${
                errors.identification_number ? "border-red-500" : "border-gray-200"
              } rounded-xl transition-all duration-300 text-gray-900 placeholder-gray-500`}
              maxLength={formData.identification_type === DNI ? DNI_LONGITUD : CARNET_LONGITUD}
            />
            {/* Loading indicator for DNI lookup */}
            {isDniLookupLoading && formData.identification_type === DNI && (
              <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 text-orange-500 animate-spin w-5 h-5" />
            )}
            {errors.identification_number && (
              <p className="text-red-500 text-xs mt-1">{errors.identification_number}</p>
            )}
            {/* DNI lookup message */}
            {dniLookupMessage && formData.identification_type === DNI && (
              <p className={`text-xs mt-1 ${
                dniLookupMessage.startsWith('✓') ? 'text-green-600' : 'text-orange-600'
              }`}>
                {dniLookupMessage}
              </p>
            )}
          </div>
        </div>

        {/* First Name */}
        <div>
          <label htmlFor="firstName" className="sr-only">
            Nombre
          </label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Nombre"
              className={`mia-input w-full pl-12 pr-4 py-3 bg-gray-50 border ${
                errors.firstName ? "border-red-500" : "border-gray-200"
              } rounded-xl transition-all duration-300 text-gray-900 placeholder-gray-500`}
            />
            {errors.firstName && (
              <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
            )}
          </div>
        </div>

        {/* Last Name */}
        <div>
          <label htmlFor="lastName" className="sr-only">
            Apellidos
          </label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Apellidos"
              className={`mia-input w-full pl-12 pr-4 py-3 bg-gray-50 border ${
                errors.lastName ? "border-red-500" : "border-gray-200"
              } rounded-xl transition-all duration-300 text-gray-900 placeholder-gray-500`}
            />
            {errors.lastName && (
              <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="sr-only">
            Teléfono
          </label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Teléfono"
              className={`mia-input w-full pl-12 pr-4 py-3 bg-gray-50 border ${
                errors.phone ? "border-red-500" : "border-gray-200"
              } rounded-xl transition-all duration-300 text-gray-900 placeholder-gray-500`}
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>
        </div>

        <div className="hidden">
          <label htmlFor="birthDate" className="sr-only">
            Fecha de Nacimiento (Opcional)
          </label>
          <div className="relative">
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={formData.birthDate}
              onChange={handleChange}
              placeholder="Fecha de nacimiento (opcional)"
              className={`mia-input w-full pl-12 pr-4 py-3 bg-gray-50 border ${
                errors.birthDate ? "border-red-500" : "border-gray-200"
              } rounded-xl transition-all duration-300 text-gray-900 placeholder-gray-500`}
              max={new Date().toISOString().split('T')[0]} // No permitir fechas futuras
            />
            {errors.birthDate && (
              <p className="text-red-500 text-xs mt-1">{errors.birthDate}</p>
            )}
          </div>
        </div>

        {/* Password fields commented out - may be needed later */}
        {/*
        <div>
          <label htmlFor="password" className="sr-only">
            Contraseña
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Contraseña"
              className={`mia-input w-full pl-12 pr-4 py-3 bg-gray-50 border ${
                errors.password ? "border-red-500" : "border-gray-200"
              } rounded-xl transition-all duration-300 text-gray-900 placeholder-gray-500`}
            />
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="sr-only">
            Confirmar Contraseña
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirmar Contraseña"
              className={`mia-input w-full pl-12 pr-4 py-3 bg-gray-50 border ${
                errors.confirmPassword ? "border-red-500" : "border-gray-200"
              } rounded-xl transition-all duration-300 text-gray-900 placeholder-gray-500`}
            />
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
            )}
          </div>
        </div>
        */}

        {/* Message */}
        <AnimatePresence>
          {message && (
            <motion.div
              className={`md:col-span-2 p-3 rounded-xl flex items-start gap-3 ${
                (typeof message === 'string' && message.includes("exitoso"))
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {(typeof message === 'string' && message.includes("exitoso")) ? (
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              )}
              <div className="text-sm font-medium">
                {message.split('\n').map((line, index) => (
                  <div key={index} className={index > 0 ? 'mt-1' : ''}>
                    {line}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isLoading}
          className="md:col-span-2 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3"
          style={{
            background: "linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)",
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {isLoading ? "Registrando..." : "Crear Cuenta"}
        </motion.button>



        {/* Login Link */}
        <p className="md:col-span-2 text-center text-gray-600 mt-4">
          ¿Ya tienes una cuenta?{" "}
          <motion.button
            onClick={onNavigateToLogin}
            className="text-orange-500 font-semibold hover:underline"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Inicia Sesión aquí
          </motion.button>
        </p>
      </form>
    </motion.div>
  );
};

export default RegistrationForm;
